import { ALGORITHM_IDS } from '~/models/schemaVersion'
import type { CalendarDate } from '~/types/dates'
import type { Kilograms, KilogramsDelta, Percent } from '~/types/units'
import { type DateWindow, isWithin, precedingWindow, trailingWindow } from './calendar'

/**
 * Trailing seven-day weight trend (D-036).
 *
 * Pure: no clock, no storage, no formatting. The reference date is supplied by
 * the caller so the same fixtures produce the same result forever.
 *
 * The rules that matter, all from D-036:
 *
 *   - the window is the seven calendar days ENDING on the reference date,
 *     inclusive;
 *   - missing days are OMITTED, never interpolated or carried forward;
 *   - an insufficient window yields no average at all, only a count — a mean of
 *     one weigh-in is not a trend, and showing it as one invites exactly the
 *     day-to-day overreaction the product exists to prevent;
 *   - the week-on-week comparison appears only when BOTH windows are
 *     sufficient.
 *
 * Rounding never happens here. Full precision is preserved and the presentation
 * layer rounds (D-041).
 */

export const TRAILING_WINDOW_DAYS = 7

export type Sufficiency = 'sufficient' | 'limited' | 'insufficient'

/**
 * Entry-count thresholds (D-036).
 *
 * Four aligns with the minimum-viable-week guidance of at least four weigh-ins.
 * These are injectable rather than baked in, and when the rules engine lands at
 * Checkpoint 1.7 its `rules/thresholds.ts` becomes the single configuration
 * home that supplies them (D-005 risk note: guidance must not become a hidden
 * universal constant).
 */
export interface SufficiencyThresholds {
  readonly sufficientMinEntries: number
  readonly limitedMinEntries: number
}

export const DEFAULT_SUFFICIENCY_THRESHOLDS: SufficiencyThresholds = {
  sufficientMinEntries: 4,
  limitedMinEntries: 2,
}

/** The minimum a calculation needs: a date and a canonical weight. */
export interface WeightObservation {
  readonly date: CalendarDate
  readonly weightKg: Kilograms
}

export interface TrendWindow {
  readonly window: DateWindow
  readonly entryCount: number
  readonly sufficiency: Sufficiency
  /** Undefined when the window is insufficient — never a placeholder number. */
  readonly averageKg: Kilograms | undefined
}

export interface TrendComparison {
  readonly changeKg: KilogramsDelta
  /** Change as a percentage of the previous average — the weekly rate. */
  readonly percentChange: Percent
}

export interface WeightTrend {
  readonly reference: CalendarDate
  readonly current: TrendWindow
  readonly previous: TrendWindow
  /** Present only when both windows are sufficient. */
  readonly comparison: TrendComparison | undefined
  readonly algorithm: typeof ALGORITHM_IDS.trend
}

export function classifySufficiency(
  entryCount: number,
  thresholds: SufficiencyThresholds,
): Sufficiency {
  if (entryCount >= thresholds.sufficientMinEntries) return 'sufficient'
  if (entryCount >= thresholds.limitedMinEntries) return 'limited'
  return 'insufficient'
}

function summarise(
  observations: readonly WeightObservation[],
  window: DateWindow,
  thresholds: SufficiencyThresholds,
): TrendWindow {
  const inWindow = observations
    .filter((observation) => isWithin(observation.date, window))
    // Sorted before summing, deliberately. Floating-point addition is not
    // associative: summing the same weights in a different order can differ in
    // the last bit (79.6 versus 79.60000000000001). Storage makes no ordering
    // guarantee, and the rules engine must produce byte-identical output for
    // identical input, so summation is pinned to a canonical order. Dates are
    // unique — the store is keyed by date — so this total order is well
    // defined.
    .sort((left, right) => (left.date as string).localeCompare(right.date as string))

  const entryCount = inWindow.length
  const sufficiency = classifySufficiency(entryCount, thresholds)

  if (sufficiency === 'insufficient') {
    return { window, entryCount, sufficiency, averageKg: undefined }
  }

  const total = inWindow.reduce((sum, observation) => sum + (observation.weightKg as number), 0)
  return { window, entryCount, sufficiency, averageKg: (total / entryCount) as Kilograms }
}

export function calculateWeightTrend(
  observations: readonly WeightObservation[],
  reference: CalendarDate,
  thresholds: SufficiencyThresholds = DEFAULT_SUFFICIENCY_THRESHOLDS,
): WeightTrend {
  const currentWindow = trailingWindow(reference, TRAILING_WINDOW_DAYS)
  const priorWindow = precedingWindow(currentWindow, TRAILING_WINDOW_DAYS)

  const current = summarise(observations, currentWindow, thresholds)
  const previous = summarise(observations, priorWindow, thresholds)

  return {
    reference,
    current,
    previous,
    comparison: compare(current, previous),
    algorithm: ALGORITHM_IDS.trend,
  }
}

/**
 * Week-on-week change, only when both windows are sufficient.
 *
 * Comparing a well-sampled week against a week with two weigh-ins would produce
 * a confident-looking number built on noise, so it is withheld rather than
 * qualified.
 */
function compare(current: TrendWindow, previous: TrendWindow): TrendComparison | undefined {
  if (current.sufficiency !== 'sufficient' || previous.sufficiency !== 'sufficient')
    return undefined
  if (current.averageKg === undefined || previous.averageKg === undefined) return undefined

  const currentAverage = current.averageKg as number
  const previousAverage = previous.averageKg as number
  if (previousAverage === 0) return undefined

  const changeKg = currentAverage - previousAverage
  return {
    changeKg: changeKg as KilogramsDelta,
    percentChange: ((changeKg / previousAverage) * 100) as Percent,
  }
}
